import React, {useState, useEffect, useContext} from 'react';
import {Routes, Route, useMatch, useParams} from "react-router";
import SearchV2 from '../TargetingV2/TargetingV2'
import Admin from '../TargetingV2/Admin/Admin'
import Spinner from '../../components/Spinner'
import apiFetch from '../../api/apiFetch'
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from '../../AppInsights';
import useTargetingPermissions from '../TargetingV2/hooks/useTargetingPermissions'
import { ExercisesContext } from '../../ExercisesContext'

function Targeting(props) {
    const match = useMatch('/targeting/*')
    const path = '/targeting'
    const url = match ? match.pathname : '/targeting'

    const exercises = useContext(ExercisesContext)
    const targetingExercises = exercises?.targeting

    const { isAdminUser, isMultiExerciseUser, isElevatedViewer } = useTargetingPermissions()
    const singleExercise = targetingExercises?.[0]

    const [loaded, setLoaded] = useState(false)
    const [approved, setApproved] = useState(false)
    const [backUrl, setBackUrl] = useState("/")

    useEffect(() => {
        if (targetingExercises) {
            setLoaded(true)
        }
    }, [targetingExercises])

    // Approval check only runs for single-exercise arms (arms 4 & 5).
    // Multi-exercise arms delegate this to Search/Admin after resolving their exercise.
    useEffect(() => {
        if (loaded && !isAdminUser && !isMultiExerciseUser) {
            const exercise = targetingExercises[0]
            apiFetch('/api/v2/targeting/approval-check', { ID: exercise.ID }).then((data) => {
                setApproved(data.length > 0)
            })
        }
    }, [loaded])

    if (!loaded) {
        return <Spinner />
    }

    // Arm 1 — isAdminUser (ADMIN + DBD/IDD on 31/33): full admin capabilities
    if (isAdminUser) {
        return (
            <Routes>
                <Route path='search/:idSlug/:terrSlug' element={<AdminSearchRouteWithParams url={backUrl} backUrl={backUrl} setBackUrl={setBackUrl} approved={approved} setApproved={setApproved} {...props} targetingExercises={targetingExercises} />} />
                <Route path='admin/:idSlug' element={<AdminRouteWithParams path={path} approved={approved} setApproved={setApproved} {...props} targetingExercises={targetingExercises} />} />
            </Routes>
        )
    }

    // Arm 2 — multi-exercise + elevated viewer: admin panel read-only.
    // Covers OAS/OBD on 21/31/33 AND cross-team elevated viewers (e.g. OBD on 13 who receives
    // both TM13 and TM15 exercises via crossTeamExerciseAccess config).
    if ((isMultiExerciseUser || targetingExercises.length > 1) && isElevatedViewer) {
        return (
            <Routes>
                <Route path='search/:idSlug/:terrSlug' element={<AdminSearchRouteWithParams url={url} approved={approved} setApproved={setApproved} {...props} targetingExercises={targetingExercises} />} />
                <Route path=':idSlug' element={<AdminRouteWithParams path={path} approved={approved} setApproved={setApproved} {...props} targetingExercises={targetingExercises} />} />
            </Routes>
        )
    }

    // Arm 3 — isMultiExerciseUser (regular reps on 21/31/33): search only
    if (isMultiExerciseUser) {
        return (
            <Routes>
                <Route path=':idSlug' element={<MultiExerciseRouteWithParams url={url} approved={approved} setApproved={setApproved} {...props} targetingExercises={targetingExercises} />} />
            </Routes>
        )
    }

    // Arms 4 & 5 — single exercise. Resolve once here; children receive a plain object.
    const exercise = singleExercise

    // Arm 4 — isElevatedViewer (OAS/OBD on 13/14/15): admin panel read-only
    if (isElevatedViewer) {
        return (
            <Routes>
                <Route path='search/:idSlug/:terrSlug' element={<SearchV2 url={url} approved={approved} {...props} exercise={exercise} />} />
                <Route path='search/:terrSlug' element={<SearchV2 url={url} approved={approved} {...props} exercise={exercise} />} />
                <Route path='*' element={<Admin path={`${path}`} approved={approved} setApproved={setApproved} {...props} exercise={exercise} />} />
            </Routes>
        )
    }

    // Arm 5 — standard single-exercise user (13/14/15)
    return <SearchV2 url={url} approved={approved} {...props} exercise={exercise} />
}

export default withAITracking(reactPlugin, Targeting);

// Param-reading wrappers for v7 Routes (render props removed; useParams must be called in a component)
function AdminSearchRouteWithParams({ targetingExercises, ...rest }) {
    const { idSlug } = useParams()
    const exercise = targetingExercises?.find(ex => ex.ID == idSlug)
    return <AdminSearchRoute key={exercise?.ID} exercise={exercise} {...rest} />
}

function AdminRouteWithParams({ targetingExercises, ...rest }) {
    const { idSlug } = useParams()
    const exercise = targetingExercises?.find(ex => ex.ID == idSlug)
    return <Admin key={exercise?.ID} exercise={exercise} {...rest} path={rest.path?.replace(':idSlug', idSlug)} />
}

function MultiExerciseRouteWithParams({ targetingExercises, ...rest }) {
    const { idSlug } = useParams()
    const exercise = targetingExercises?.find(ex => ex.ID == idSlug)
    return <MultiExerciseRoute key={exercise?.ID} exercise={exercise} {...rest} />
}

// TargetingV2 is the unconditional baseline — Search (v1) is retired
function AdminSearchRoute({ exercise, ...rest }) {
    if (!exercise) return <Spinner />
    return <SearchV2 exercise={exercise} {...rest} />
}

// TargetingV2 is the unconditional baseline — Search (v1) is retired
function MultiExerciseRoute({ exercise, ...rest }) {
    if (!exercise) return <Spinner />
    return <SearchV2 exercise={exercise} {...rest} />
}
